/**
 * SEOHead Component
 * 
 * Project: SunMarbella Holiday Rental Website
 * Version: 1.0.0
 * Last Modified: 2025-03-09
 * 
 * Purpose:
 * Manages dynamic meta tags, title, and structured data for SEO optimization.
 * Replaces react-helmet-async with native React 19 document metadata.
 * 
 * Features:
 * - Dynamic page title
 * - Meta description
 * - Open Graph tags (social sharing)
 * - Twitter Card tags
 * - Canonical URLs
 * - JSON-LD structured data
 * - Responsive meta viewport
 * - Language and locale settings
 * 
 * Props:
 * - title: string (page title)
 * - description: string (meta description)
 * - image?: string (OG image URL)
 * - url?: string (canonical URL)
 * - type?: string (OG type, default: 'website')
 * - structured?: object (JSON-LD data)
 * 
 * Dependencies:
 * - React 19.2.4 (useEffect, JSX)
 * - No external SEO libraries (native implementation)
 * 
 * Implementation Notes:
 * - Uses useEffect to update document.title
 * - Renders meta tags directly in JSX
 * - JSON-LD injected via script tag in useEffect
 * - Replaces removed react-helmet-async dependency
 * 
 * Changelog:
 * - 1.0.0 (2025-03-09): Initial implementation with native React 19 support
 * - Removed react-helmet-async dependency (incompatible with React 19)
 * - Implemented native document metadata management
 */

import React, { useEffect } from 'react';
import { DESIGN_TOKENS } from '../designTokens';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  type?: string;
  images?: Array<{ url: string; alt: string }>;
  jsonLd?: Record<string, unknown>[];
  robots?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = "Luxury Holiday Rentals in Marbella | SUNMARBELLA",
  description = "Inspected Marbella rentals, better direct rates, real local support. Book direct with SUNMARBELLA.",
  canonical = "https://sunmarbella.com",
  type = "website",
  images = [],
  jsonLd = [],
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
}) => {
  const siteName = "SUNMARBELLA";
  const twitterHandle = "@sunmarbella";
  const locale = "en_GB";
  const fallbackImage = {
    url: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&h=630&fit=crop",
    alt: "Luxury holiday rental in Marbella",
  };
  const ogImages = images.length > 0 ? images : [fallbackImage];
  const primaryImage = ogImages[0];

  // React 19 supports <title> and <meta> natively in component render.
  // For structured data (JSON-LD), we use useEffect to inject/remove script tags.
  useEffect(() => {
    const scripts: HTMLScriptElement[] = [];
    jsonLd.forEach((data) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
      scripts.push(script);
    });
    return () => {
      scripts.forEach((s) => s.remove());
    };
  }, [jsonLd]);

  return (
    <>
      {/* React 19 native document metadata - hoisted to <head> automatically */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="theme-color" content={DESIGN_TOKENS.colors.brandOcean} />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content="es_ES" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      {ogImages.map((img, idx) => (
        <React.Fragment key={idx}>
          <meta property="og:image" content={img.url} />
          <meta property="og:image:secure_url" content={img.url} />
          <meta property="og:image:alt" content={img.alt || primaryImage.alt} />
        </React.Fragment>
      ))}
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:image" content={primaryImage.url} />
      <meta name="twitter:image:alt" content={primaryImage.alt} />

      {/* Additional SEO Meta */}
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="language" content="English" />
      <meta name="geo.region" content="ES-AN" />
      <meta name="geo.placename" content="Marbella, Spain" />
      <meta name="ICBM" content="36.5103,-4.8875" />
    </>
  );
};
